import React, { useContext, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogContent, IconButton } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  COLUMN_LABEL_MAP,
  DEFAULT_PRODUCT_TABLE_COLUMNS,
  PRODUCT_TABLE_COLUMNS_SETTING,
  USER_ROLES,
} from '../constants';
import Table from '../components/Table';
import { getAllProducts, useCreateProduct, useDeleteProduct, useUpdateProduct } from '../queries/products';
import ProductDetailsForm from './components/ProductDetailsForm';
import ConfirmDialog from '../components/ConfirmDialog';
import PageShell from '../components/PageShell';
import { UserContext } from '../context/UserContext';
import { getAllCompanies } from '../queries/companies';
import DownloadButton from '../components/DownloadButton';
import ColumnsConfigureDrawer from '../components/ColumnsConfigureDrawer';
import CustomLink from '../components/CustomLink';
import { getSettingByTitle, useUpdateSetting } from '../queries/settings';
import { formatValidNumber } from '../utils/helper';
import { requestUnderstandLeave } from '../utils/understandLeave';
import CompanyLogo from '../components/CompanyLogo';
import {
  ClientWorkspaceDrawer,
  ClientWorkspaceOpenDoor,
  ClientWorkspaceHandleReply,
  TABS,
} from '../components/ClientWorkspace';

const ProductsPage = () => {
  const { user } = useContext(UserContext);

  const { data: products, isRefetching, refetch } = useQuery(getAllProducts({}));
  const { data: companies } = useQuery(getAllCompanies({}));
  const { data: columnsSetting, refetch: refetchSetting } = useQuery(
    getSettingByTitle({ params: { title: PRODUCT_TABLE_COLUMNS_SETTING } }),
  );

  const { mutate: createProduct, isPending: isCreatingProduct } = useCreateProduct();
  const { mutate: updateProduct, isPending: isUpdatingProduct } = useUpdateProduct();
  const { mutate: deleteProduct, isPending: isDeletingProduct } = useDeleteProduct();
  const { mutate: updateSetting } = useUpdateSetting();

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState();
  const [selectedProduct, setSelectedProduct] = useState();
  const [workspaceProduct, setWorkspaceProduct] = useState(null);
  const [workspaceInitialTab, setWorkspaceInitialTab] = useState(TABS.OVERVIEW);
  const [openDoorProduct, setOpenDoorProduct] = useState(null);
  const [handleReplyProduct, setHandleReplyProduct] = useState(null);

  const isAdmin = user?.role === USER_ROLES.ADMIN;
  const isLeadAgent = user?.role === USER_ROLES.LEAD_AGENT;

  const columns = useMemo(() => {
    const raw = columnsSetting?.[0]?.content;
    if (!raw || typeof raw !== 'string') return DEFAULT_PRODUCT_TABLE_COLUMNS;
    const trimmed = raw.trim();
    // Only attempt JSON.parse when content looks like JSON (array or object)
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) {
      return DEFAULT_PRODUCT_TABLE_COLUMNS;
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : DEFAULT_PRODUCT_TABLE_COLUMNS;
    } catch {
      return DEFAULT_PRODUCT_TABLE_COLUMNS;
    }
  }, [columnsSetting]);

  const visibleColumns = useMemo(() => {
    try {
      const visibleColumns = columns
        .filter((column) => !column.hidden)
        .map((column) => ({ ...column, label: COLUMN_LABEL_MAP[column.key] || column.label }));
      return isAdmin ? [...visibleColumns, { key: 'actions', label: '', width: '100px' }] : visibleColumns;
    } catch (error) {
      console.error('Failed to parse columns', error);
      return [];
    }
  }, [columns, isAdmin]);

  const stopRowClick = (event) => {
    event.stopPropagation();
  };

  const tableData = useMemo(
    () =>
      products?.map((product) => ({
        companyName: product.company?.companyName,
        ...product,
        averageMRR: product.averageMRR || 'N/A',
        commissionFirstTerm: formatValidNumber(product.commissionFirstTerm),
        commissionRenewals: formatValidNumber(product.commissionRenewals),
        // averageTotalCommissionValue: formatValidNumber(product.averageTotalCommissionValue),
        averageMonthCommissionValue: formatValidNumber(product.averageMonthCommissionValue),
        averageConversionRate: formatValidNumber(product.averageConversionRate),
        churn: formatValidNumber(product.churn),
        averageSalesCycle: product.averageSalesCycle || 'N/A',
        duration: product.duration || 'No Limit',
        website:
          product.website || product.company?.website ? (
            <Box
              color='inherit'
              component={CustomLink}
              to={product.website || product.company?.website}
              target='_blank'
              onClick={stopRowClick}
              sx={{
                display: 'block',
                textTransform: 'none',
                width: 'max-content',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {product.website || product.company?.website}
            </Box>
          ) : undefined,
        markets: product.markets?.join(', '),
        companyLogo: <CompanyLogo url={product.company?.companyLogo} size='50px' />,
        actions: (
          <Box display='flex' justifyContent='end' onClick={stopRowClick}>
            <IconButton
              color='primary'
              onClick={() => handleSelectProduct({ ...product, company: product.company?._id })}
            >
              <EditIcon />
            </IconButton>
            <IconButton color='error' disabled={isDeletingProduct} onClick={() => setSelectedProductId(product._id)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
        downloads: (
          <Box display='flex' flexDirection='column' gap={1} onClick={stopRowClick}>
            {product.downloads?.map((download, index) => (
              <DownloadButton key={index} fileUrl={download.url} fileName={download.description} />
            ))}
          </Box>
        ),
      })) || [],
    [products, isDeletingProduct],
  );

  const handleOpenWorkspace = (row) => {
    const product = products?.find((item) => item._id === row._id);
    if (!product) return;
    const openProduct = () => {
      setWorkspaceInitialTab(TABS.OVERVIEW);
      setWorkspaceProduct(product);
    };
    if (workspaceProduct && String(workspaceProduct._id) !== String(product._id)) {
      requestUnderstandLeave(openProduct);
      return;
    }
    openProduct();
  };

  const handleCloseWorkspace = () => {
    setWorkspaceProduct(null);
    setWorkspaceInitialTab(TABS.OVERVIEW);
  };

  const handleOpenDoor = () => {
    if (!workspaceProduct) return;
    setOpenDoorProduct(workspaceProduct);
    setWorkspaceProduct(null);
  };

  const handleBackFromOpenDoor = () => {
    if (!openDoorProduct) return;
    setWorkspaceInitialTab(TABS.AI);
    setWorkspaceProduct(openDoorProduct);
    setOpenDoorProduct(null);
  };

  const handleOpenHandleReply = () => {
    if (!workspaceProduct) return;
    setHandleReplyProduct(workspaceProduct);
    setWorkspaceProduct(null);
  };

  const handleBackFromHandleReply = () => {
    if (!handleReplyProduct) return;
    setWorkspaceInitialTab(TABS.AI);
    setWorkspaceProduct(handleReplyProduct);
    setHandleReplyProduct(null);
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setIsProductModalOpen(true);
  };

  const onConfirmProduct = (data) => {
    if (!selectedProduct) {
      createProduct(
        { data },
        {
          onSuccess: () => {
            refetch();
            setIsProductModalOpen(false);
            toast('Created product successfully!', { type: 'success' });
          },
          onError: (error) => {
            const message = error?.response?.data?.error || error?.message || 'Failed to create product.';
            toast(message, { type: 'error' });
          },
        },
      );
    } else {
      updateProduct(
        { id: selectedProduct._id, data },
        {
          onSuccess: () => {
            refetch();
            setIsProductModalOpen(false);
            toast('Updated product successfully!', { type: 'success' });
          },
          onError: (error) => {
            const message = error?.response?.data?.error || error?.message || 'Failed to update product.';
            toast(message, { type: 'error' });
          },
        },
      );
    }
  };

  const handleDeleteProduct = () => {
    deleteProduct(
      { id: selectedProductId },
      {
        onSuccess: () => {
          refetch();
          setSelectedProductId(undefined);
          toast('Deleted product successfully!', { type: 'success' });
        },
        onError: (error) => {
          const message = error?.response?.data?.error || error?.message || 'Failed to delete product.';
          toast(message, { type: 'error' });
        },
      },
    );
  };

  const handleConfirmColumnsSetting = (updatedColumns) => {
    updateSetting(
      {
        data: {
          title: PRODUCT_TABLE_COLUMNS_SETTING,
          content: JSON.stringify(updatedColumns),
        },
      },
      {
        onSuccess: () => {
          refetchSetting();
          setIsConfigureModalOpen(false);
          toast('Updated columns setting successfully!', { type: 'success' });
        },
        onError: (error) => {
          const message = error?.response?.data?.error || error?.message || 'Failed to update columns setting.';
          toast(message, { type: 'error' });
        },
      },
    );
  };

  if (openDoorProduct) {
    const companyName = openDoorProduct.company?.companyName || openDoorProduct.companyName || 'this company';

    return (
      <Box display='flex' flexDirection='column' height='100%' minHeight={0} bgcolor='#fff' sx={{ flex: 1 }}>
        <ClientWorkspaceOpenDoor companyName={companyName} onBack={handleBackFromOpenDoor} />
      </Box>
    );
  }

  if (handleReplyProduct) {
    const companyName = handleReplyProduct.company?.companyName || handleReplyProduct.companyName || 'this company';

    return (
      <Box display='flex' flexDirection='column' height='100%' minHeight={0} bgcolor='#fff' sx={{ flex: 1 }}>
        <ClientWorkspaceHandleReply companyName={companyName} onBack={handleBackFromHandleReply} />
      </Box>
    );
  }

  return (
    <>
      <PageShell
        title='Opportunities'
        subtitle={isLeadAgent ? 'View opportunities and select the client and product you want to work on.' : undefined}
        actions={
          isAdmin ? (
            <Box display='flex' gap={1}>
              <Button variant='contained' onClick={() => handleSelectProduct(undefined)}>
                Add Product
              </Button>
              <Button variant='outlined' onClick={() => setIsConfigureModalOpen(true)}>
                Configure
              </Button>
            </Box>
          ) : null
        }
      >
        <Table
          isLoading={isRefetching}
          columns={visibleColumns}
          data={tableData}
          onRowClick={isLeadAgent ? handleOpenWorkspace : undefined}
          selectedRowId={isLeadAgent ? workspaceProduct?._id : undefined}
        />
      </PageShell>

      {isLeadAgent && (
        <ClientWorkspaceDrawer
          open={!!workspaceProduct}
          onClose={handleCloseWorkspace}
          product={workspaceProduct}
          initialTab={workspaceInitialTab}
          onOpenDoor={handleOpenDoor}
          onHandleReply={handleOpenHandleReply}
        />
      )}

      <ColumnsConfigureDrawer
        open={isConfigureModalOpen}
        columns={columns}
        onClose={() => setIsConfigureModalOpen(false)}
        onConfirm={handleConfirmColumnsSetting}
        onReset={() => handleConfirmColumnsSetting(DEFAULT_PRODUCT_TABLE_COLUMNS)}
      />

      <Dialog
        open={isProductModalOpen}
        maxWidth='lg'
        disableRestoreFocus
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          setIsProductModalOpen(false);
        }}
      >
        <DialogContent>
          <ProductDetailsForm
            isAdmin={isAdmin}
            companies={companies}
            productData={selectedProduct}
            isLoading={isRefetching || isCreatingProduct || isUpdatingProduct}
            onConfirm={onConfirmProduct}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!selectedProductId}
        onClose={() => setSelectedProductId(undefined)}
        title='Delete Product'
        content='Are you sure you want to delete this product?'
        confirmButton='Delete'
        confirmButtonProps={{ color: 'error', variant: 'contained' }}
        onConfirm={handleDeleteProduct}
      />
    </>
  );
};

export default ProductsPage;
